import React, { useState, useEffect, useRef, useMemo } from "react";
import Modal from "./Modal";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { PROJECT_LAYOUT } from "../data/layout";

const isRowLayout = PROJECT_LAYOUT === "row";

// Rendered image width in the single-column layout, so the browser can choose
// between the 810px grid image and the 1400px stack image
const STACK_IMAGE_SIZES =
  "(min-width: 741px) 700px, (min-width: 375px) calc(100vw - 48px), calc(100vw - 32px)";

export default function Project({ title, images, description, pairFirstTwo }) {
  const [itemWidth, setItemWidth] = useState(null);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
  const rowRef = useRef(null);

  // Refs for video elements to manage playback
  const videoRefs = useRef({});

  useEffect(() => {
    // The stacked layout is sized entirely in CSS; only the 3-across row needs JS sizing
    if (!isRowLayout) return;

    const calculateItemWidth = () => {
      if (!rowRef.current) return;

      const currentScreenWidth = window.innerWidth;
      let calculatedWidth;

      if (currentScreenWidth > 1400) {
        // Largest breakpoint - 3 columns
        const rowWidth = rowRef.current.clientWidth;
        const columnsCount = 3;
        const gapsWidth = (columnsCount - 1) * 24;
        calculatedWidth = (rowWidth - gapsWidth) / columnsCount;
      } else if (currentScreenWidth > 1079) {
        // Second largest breakpoint - 3 columns
        const rowWidth = rowRef.current.clientWidth;
        const columnsCount = 3;
        const gapsWidth = (columnsCount - 1) * 24;
        calculatedWidth = (rowWidth - gapsWidth) / columnsCount;
      } else if (currentScreenWidth > 740) {
        // Medium breakpoint - fixed width
        calculatedWidth = 384;
      } else if (currentScreenWidth > 374) {
        // Small breakpoint - fixed width
        calculatedWidth = 315;
      } else {
        // Very small breakpoint - fixed width
        calculatedWidth = 240;
      }

      setItemWidth(calculatedWidth);
      setScreenWidth(currentScreenWidth);
    };

    // Calculate initial width
    calculateItemWidth();

    // Add resize listener
    window.addEventListener("resize", calculateItemWidth);

    // Cleanup listener
    return () => {
      window.removeEventListener("resize", calculateItemWidth);
    };
  }, []);

  // Improved video playback management
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1 // Trigger when at least 10% of the video is visible
    };

    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const videoElement = entry.target;
        const videoKey = videoElement.getAttribute('data-key');

        if (entry.isIntersecting) {
          // Play video when it becomes visible
          videoElement.play().catch(error => {
            console.warn('Autoplay was prevented:', error);
          });
        } else {
          // Pause video when it's not visible
          videoElement.pause();
        }
      });
    }, observerOptions);

    // Observe all video elements
    Object.values(videoRefs.current).forEach(videoEl => {
      if (videoEl) videoObserver.observe(videoEl);
    });

    // Cleanup observer
    return () => {
      Object.values(videoRefs.current).forEach(videoEl => {
        if (videoEl) videoObserver.unobserve(videoEl);
      });
    };
  }, [images]);

  const openModal = (media, index) => {
    setSelectedMedia(media);
    setSelectedMediaIndex(index);
  };

  const closeModal = () => {
    setSelectedMedia(null);
    setSelectedMediaIndex(null);
  };

  const navigateMedia = (direction) => {
    if (selectedMediaIndex === null) return;

    const newIndex = (selectedMediaIndex + direction + images.length) % images.length;
    setSelectedMedia(images[newIndex]);
    setSelectedMediaIndex(newIndex);
  };

  const mediaItems = images.map((media, index) => (
    <div
      key={index}
      className="project-media-item"
      style={
        isRowLayout
          ? {
              width: itemWidth ? `${itemWidth}px` : "auto",
              height: itemWidth ? `${itemWidth / 1.36}px` : "auto",
              cursor: "pointer",
            }
          : undefined
      }
      onClick={() => openModal(media, index)}
    >
      {media.type === 'image' ? (
        <img
          src={media.src}
          srcSet={
            !isRowLayout && media.stackSrc
              ? `${media.src} 810w, ${media.stackSrc} 1400w`
              : undefined
          }
          sizes={!isRowLayout ? STACK_IMAGE_SIZES : undefined}
          alt={`${title} - image ${index + 1}`}
          className="project-image"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <video
          ref={el => videoRefs.current[index] = el}
          src={media.src}
          alt={`${title} - video ${index + 1}`}
          className="project-video"
          data-key={index}
          muted
          playsInline
          preload="metadata"
          loop
        />
      )}
    </div>
  ));

  const projectInfo = (
    <div className="project-info">
      <h2>{title}</h2>
      {description && <p className="project-description">{description}</p>}
    </div>
  );

  return (
    <>
      {isRowLayout ? (
        <div className="project-container">
          <div className="project-media-row" ref={rowRef}>
            {mediaItems}
          </div>
          {projectInfo}
        </div>
      ) : (
        <div className="project-container project-container-stack">
          {projectInfo}
          <div className="project-media-stack">
            {pairFirstTwo ? (
              <>
                <div className="project-media-pair">{mediaItems.slice(0, 2)}</div>
                {mediaItems.slice(2)}
              </>
            ) : (
              mediaItems
            )}
          </div>
        </div>
      )}

      {selectedMedia && (
        <Modal 
          onClose={closeModal}
          mediaList={images}
          initialIndex={selectedMediaIndex}
          onNavigate={navigateMedia}
        >
          {selectedMedia.type === 'image' ? (
            <img
              src={selectedMedia.fullResSrc}
              alt={`${title} - full size`}
              className="modal-image"
            />
          ) : (
            <video
              src={selectedMedia.fullResSrc}
              alt={`${title} - full size`}
              className="modal-video modal-media"
              autoPlay
              playsInline
              muted
              loop
              controls={false}
            />
          )}
        </Modal>
      )}
    </>
  );
}